import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { Router, UrlTree } from '@angular/router';
import { AlertsService, CLToastType } from '@clavisco/alerts';
import { IToken } from '../core/interfaces/i-token';
import { AuthenticationService } from '../core/services/authentication.service';
import { DataStorageService } from '../core/services/data-storage.service';

/**
 * AuthenticationInterceptor intercepts HTTP requests to add authentication tokens
 * and handle errors related to authentication.
 */
@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  /**
   * Constructs the AuthenticationInterceptor.
   *
   * @param {AlertsService} alertService - Service for displaying alerts and notifications.
   * @param {AuthenticationService} authService - Service for managing authentication operations.
   * @param {DataStorageService} dataStorageService - Service for managing data storage.
   * @param {Router} router - Angular Router for navigation.
   */
  constructor(
    private alertService: AlertsService,
    private authService: AuthenticationService,
    private dataStorageService: DataStorageService,
    private router: Router
  ) {}

  /**
   * Intercepts HTTP requests to add authentication tokens and handle errors.
   *
   * @param {HttpRequest<unknown>} request - The outgoing HTTP request.
   * @param {HttpHandler} next - The next interceptor in the chain.
   * @returns {Observable<HttpEvent<unknown>>} An observable of the HTTP event.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    try {
      const excluedUrls: string[] = ['assets', 'token', 'ChangeRecoverPassword', 'RecoverPassword', 'ChangePassword'];
      if (excluedUrls.some(url => request.url.includes(url))) {
        return next.handle(request);
      }
      request = this.AddAuthenticationToken(request);

      return next.handle(request).pipe(
        map((event: HttpEvent<any>) => {
          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      );
    } catch (error) {
      this.alertService.Toast(
        {
          message: 'Su sesión ha expirado, por favor inicie sesión nuevamente',
          type: CLToastType.ERROR,
          duration: 5000
        }
      );
      return throwError(error);
    }
  }

  /**
   * Adds an authentication token to the HTTP request headers.
   *
   * @param {HttpRequest<any>} request - The outgoing HTTP request.
   * @returns {HttpRequest<any>} The modified HTTP request with authentication headers.
   * @throws {Error} Throws an error if the user is not authenticated.
   */
  private AddAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    if (!this.authService.IsAuthenticated()) {
      this.authService.Logout();
      const urlTree: UrlTree = this.router.createUrlTree(['/login'], {});
      this.router.navigateByUrl(urlTree);
      throw new Error();
    }

    const TOKEN = this.dataStorageService.GetItem<IToken>('currentUser');

    if (!TOKEN) {
      return request;
    }

    let headers: HttpHeaders = request.headers.set(
      `Authorization`,
      `Bearer ${TOKEN.access_token}`
    );

    request = request.clone({ headers });
    return request;
  }
}
